<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { computed, ref, watch } from "vue";

import RsLoading from "@/components/Loading.vue";
import RsViewer from "./Viewer.vue";
import RsFilePassword from "./FilePassword.vue";

import { caseFileService } from "@/services";
import { isPasswordProtected, isRectangleAnnotation, isThisMe } from "@/services/UtilitiesService";

import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useLoadingStore } from "@/stores/loading-store";
import { useSnippetStore } from "@/stores/snippets-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { Metadata } from "@/models/Metadata";

import type { HighlightCustomArea } from "@/models/Viewer";
import type UnPasswordResult from "@/models/UnPasswordResult";
import type { ViewingSession } from "@/models/ViewingSession";
import type { DockviewPanelComponentEmits } from "@/hooks/use-case-panel";
import type { ParsedMark } from "@/models/Mark";
import type { RectangleAnnotation } from "@/models/Annotation";

const routeParams = useCaseRouteParams();

withDefaults(defineProps<{
  file: Metadata;
  isVisible: boolean;
  viewerScale: { width: number; height: number };
  selectedArea: HighlightCustomArea | undefined;
}>(), {  })
defineEmits<{ reloadAnnotations: [] } & DockviewPanelComponentEmits>();

let session = _ref<ViewingSession | null>(null);
const mainViewerContainer = ref<InstanceType<typeof RsViewer>>();

const fileLoadingStatus = ref<ViewerLoadingState>("IDLE");
const fileError = ref<
  (FileLoadFailedEvent | FileRetryExhaustedEvent | FileUnknownLoadFailedEvent) & {
    errorCount: number;
    hasManuallyRetried: boolean;
  }
>();

const fileStatus = computed<ViewerControlStatus>(() => {
  const currentFile = useCaseFileStore().currentFile;
  if (currentFile == null) return "INIT";

  if (isPasswordProtected(currentFile)) return "PASSWORD_PROTECTED";

  if (hasError(currentFile)) return "HAS_ERROR";

  if (currentFile.edxProcessingStatus === "justUploaded") {
    if (useBrandStore().viewerConfig.apiVersion === "v2") {
      return "JUST_UPLOADED";
    }
  }

  if (fileLoadingStatus.value === "ERROR" && fileError.value != null) {
    switch (fileError.value.status) {
      case 404:
        return fileError.value.data?.subStatusCode > 0 && fileError.value.data?.subStatusCode < 5
          ? "EXPIRED"
          : "FAILED";
      case 503:
        if (fileError.value.data.retryAfterMs) {
          return "RETRY";
        }
        break;
      default:
        return "ERROR";
    }
  }

  if (fileLoadingStatus.value === "LOADING") return "LOADING";

  if (session.value == null) return "INIT";

  return "SUCCESS";
});

const hasViewingSession = _computed(() => {
  return fileStatus.value === "SUCCESS" && Object.keys(session.value || {}).length > 0 && session.value!.viewingSessionId;
});

const snippets = _computed(() => {
  return useSnippetStore()
    .entries.filter((m) => isThisMe(__props.file, m.fileReference))
    .map((m) => JSON.parse(m.snippetData));
});

const hasError = (updatedFileMetadata: Metadata) => {
  return (
    updatedFileMetadata.edxProcessingStatus === "issue" ||
    updatedFileMetadata.passwordStatus === "missing" ||
    updatedFileMetadata.ingrestProcessingError
  );
};

watch(
  fileStatus,
  async (newStatus) => {
    if (hasViewingSession.value) {
      return;
    }

    if (
      // Reload when status is INIT, RETRY, previously BURNING or JUST_UPLOADED but not anymore
      ["INIT", "JUST_UPLOADED"].includes(newStatus) ||
      // Reload when there was an error loading initially and the errors haven't reached the threshold to report the error
      (fileLoadingStatus.value === "ERROR" && (fileError.value?.errorCount ?? 0) < 1)
    ) {
      await loadSession();
    }
  },
  { immediate: true },
);

async function loadSession(isOnDemand = false, createNew = false) {
  let timer;

  try {
    onFileLoadingStatusChange("LOADING");
    useCaseFileStore().setCommittedRedactionsState(__props.file.fileReference, false);

    // display holding message if session takes longer than expected to load
    const msg = "We are still starting your session. Please hold on for few more seconds. Thanks for you patience.";
    timer = window.setTimeout(
      () => !hasViewingSession.value && useLoadingStore().updateLoadingMessage("session-loading", msg),
      15000,
    );

    let pageNumber = 1;

    if (routeParams.value.casefile === __props.file.fileReference) {
      if (routeParams.value.annotation) {
        const annotation = useAnnotationStore().annotations.find(
          (a): a is ParsedMark<RectangleAnnotation> =>
            a.markId === routeParams.value.annotation && isRectangleAnnotation(a.parsedMarkData),
        );
        if (annotation != null) pageNumber = annotation.parsedMarkData.pageNumber;
      } else if (routeParams.value.filepage) {
        pageNumber = +routeParams.value.filepage;
      }
    }

    session.value = await caseFileService.createSession({
      client: routeParams.value.client,
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: __props.file.fileReference,
      pageNumber: pageNumber || 1,
      isOnDemand,
      createNew,
    });
  } catch (e) {
    fileLoadError(e as FileLoadFailedEvent | FileUnknownLoadFailedEvent);
  } finally {
    window.clearTimeout(timer);
  }
}

function clearFileError() {
  onFileLoadingStatusChange("IDLE");
  refresh();
}

function createNewSessionAfterError() {
  fileError.value = undefined;
  loadSession(false, true);
}

function retryExistingSessionAfterError() {
  if (fileError.value == null) return;

  clearFileError();
  fileError.value.hasManuallyRetried = true;
}

function fileLoadError(evt?: FileLoadFailedEvent | FileRetryExhaustedEvent | FileUnknownLoadFailedEvent) {
  if (evt) console.error(evt);

  if (evt && (evt.status === 404 || evt.status === 503)) {
    // If this is coming from the viewing session piece, make sure it is for this viewer control
    if ("viewingSession" in evt && evt.viewingSession !== session.value?.viewingSessionId) return;

    session.value = null;
    fileError.value = {
      ...evt,
      errorCount: fileError.value?.errorCount ?? 0,
      hasManuallyRetried: fileError.value?.hasManuallyRetried ?? false,
    };
  } else {
    console.error("Unknown Page Load Error");

    session.value = null;
    fileError.value = {
      status: 0,
      errorCount: fileError.value?.errorCount ?? 0,
      hasManuallyRetried: fileError.value?.hasManuallyRetried ?? false,
    };
  }

  fileError.value.errorCount = fileError.value.errorCount + 1;

  onFileLoadingStatusChange("ERROR");
}

function onRedactionCommitted() {
  session.value = null;
  useCaseFileStore().setCommittedRedactionsState(__props.file.fileReference, true);
}

function onResultReceived(e: UnPasswordResult): void {
  if (!session.value) return;
  session.value = {
    ...session.value,
    viewingSessionId: e.viewingSessionId,
  };
}

async function startPreConversion(): Promise<void> {
  try {
    onFileLoadingStatusChange("LOADING");

    useLoadingStore().updateLoadingMessage("session-loading", "Requesting pre-conversion now");
    await caseFileService.preConvert(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      __props.file.fileReference,
    );
    // Pull the case data again to get the edxPreConvertStatus
    await useMetadataStore().refresh();
    // This triggers polling for metadata updates
    useMetadataStore().passwordStatusPending();
    // Log the message to the user
    useMessageStore().updateMessage({
      message: "Your request for a pre-conversion was successful.  It will be done at the next available opportunity",
      type: "success",
      title: "Pre-Convert",
      autoHide: true,
      showReload: false,
    });
  } catch (exception) {
    // Need to log this as an error
    useMessageStore().updateMessage({
      message:
        "Sorry, we couldn't action your request for pre-conversion of this document.  Please refresh the page and try again",
      type: "error",
      title: "Pre-Convert",
      autoHide: true,
      showReload: false,
    });
  }
}

function onFileLoadingStatusChange(state: ViewerLoadingState) {
  if (state === "SUCCESS") clearFileError();
  fileLoadingStatus.value = state;
}

async function refresh(): Promise<void> {
  await useMetadataStore().refresh();
  useCaseFileStore().setCommittedRedactionsState(__props.file.fileReference, false);
}

interface FileLoadFailedEvent {
  viewingSession: string;
  status: 404;
  pageNumber: number;
  data: { subStatusCode: number };
}

interface FileUnknownLoadFailedEvent {
  status: 0;
}

interface FileRetryExhaustedEvent {
  viewingSession: string;
  status: 503;
  data: { retryAfterMs: number };
}

type ViewerControlStatus =
  | "INIT"
  | "HAS_ERROR"
  | "BURNING"
  | "PASSWORD_PROTECTED"
  | "FAILED"
  | "JUST_UPLOADED"
  | "EXPIRED"
  | "RETRY"
  | ViewerLoadingState;

export type ViewerLoadingState = "IDLE" | "LOADING" | "ERROR" | "SUCCESS";
</script>
<template>
  <div class="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
    <div v-if="fileStatus === 'HAS_ERROR'" class="tw-flex tw-h-full tw-min-w-0 tw-flex-col tw-justify-between tw-p-5">
      <div class="tw-flex-1">
        <p v-if="file.ingestProcessingError" class="tw-mb-1 tw-text-xl">Error occurred while processing this record.</p>
        <h3 class="tw-mb-1 tw-mt-3 tw-text-xl">Are you sure you want to try to open this record?</h3>
        <p class="tw-text-lg">
          If yes, please click
          <a href="#" class="tw-font-bold" @click.prevent="loadSession()"> here </a>
          to open.
        </p>
      </div>
      <p v-if="file.ingestProcessingError" class="tw-mt-8 tw-font-bold">Error reported by the system:</p>
      <p v-if="file.ingestProcessingError" class="tw-mt-8 tw-break-words tw-text-red-700">
        {{ file.ingestProcessingError }}
      </p>
    </div>
    <div
      v-else-if="
        fileStatus === 'EXPIRED' || fileStatus === 'FAILED' || fileStatus === 'RETRY' || fileStatus === 'ERROR'
      "
      class="tw-flex tw-h-full tw-min-w-0 tw-max-w-xl tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-text-balance tw-p-5 tw-text-center"
    >
      <template v-if="fileStatus === 'EXPIRED'">
        <p>Your session has expired due to inactivity.</p>
      </template>
      <template v-else-if="fileStatus === 'FAILED'">
        <p>This record failed to open last time you tried.</p>
        <p v-if="file.ingestProcessingError">Error reported by the system is "{{ file.ingestProcessingError }}"</p>
      </template>
      <template v-else>
        <p>This record failed to open after repeated tries.</p>
        <p>If you continue to receive this message, please get in touch with the support team.</p>
      </template>
      <div class="tw-flex tw-gap-2 tw-divide-x-2 tw-divide-gray-500/70 tw-px-3">
        <div
          v-if="fileStatus !== 'EXPIRED'"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-2"
        >
          <p>Use this button to retry your existing session for this document.</p>
          <button
            type="button"
            class="tw-rounded-full tw-border tw-bg-black tw-px-4 tw-py-1 tw-text-white tw-transition-colors hover:tw-bg-white hover:tw-text-black"
            @click.prevent="retryExistingSessionAfterError"
          >
            Retry Existing
          </button>
        </div>
        <div
          v-if="fileError?.hasManuallyRetried"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-2"
        >
          <p>Use this button to start a new session for this document.</p>
          <button
            type="button"
            class="tw-rounded-full tw-border tw-bg-white tw-px-4 tw-py-1 tw-text-black tw-transition-colors hover:tw-bg-black hover:tw-text-white"
            @click.prevent="createNewSessionAfterError"
          >
            Start New Session
          </button>
        </div>
      </div>
    </div>
    <div v-else-if="fileStatus === 'PASSWORD_PROTECTED'" class="tw-min-w-0 tw-p-3">
      <RsFilePassword :file="file" @result-received="onResultReceived" />
    </div>
    <div v-else-if="fileStatus === 'JUST_UPLOADED'" class="tw-min-w-0 tw-p-5">
      <p class="tw-mb-1 tw-text-xl">This record has just been uploaded and will be ready for viewing shortly.</p>
      <p class="tw-mb-1 tw-mt-3 tw-text-lg">You can refresh the status of the file to see if it is ready to view.</p>
      <p class="tw-mt-3 tw-text-lg">
        To refresh, please click
        <a href="#" class="tw-font-bold" @click.prevent="refresh()">here.</a>
      </p>
    </div>
    <RsLoading v-else-if="fileStatus === 'INIT'" id="init-loading" message="Loading record details" />
    <RsViewer
      v-else-if="session != null && session.viewingSessionId"
      ref="mainViewerContainer"
      :key="session.viewingSessionId"
      :session="session"
      :file="file"
      :snippets="snippets"
      :is-visible="isVisible"
      :viewer-scale="viewerScale"
      :selected-area="selectedArea"
      :retry-count="fileError?.errorCount"
      :file-loading-state="fileLoadingStatus"
      @file-load-error="fileLoadError"
      @start-pre-convert="startPreConversion"
      @switch-on-demand="() => loadSession(true)"
      @redaction-committed="onRedactionCommitted"
      @switch-viewing-package="() => loadSession(false)"
      @reload-annotations="() => $emit('reloadAnnotations')"
      @viewer-loaded="() => onFileLoadingStatusChange('SUCCESS')"
      @panel-popout-action="(arg) => $emit('panelPopoutAction', arg)"
    />
    <RsLoading
      v-else
      id="session-loading"
      :message="`Starting viewing session (Attempt ${(fileError?.errorCount ?? 0) + 1} of ${Math.max(2, (fileError?.errorCount ?? 0) + 1)})`"
    />
  </div>
</template>
